import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import { Http } from 'Utils/Http';

const AI_NEW_THREAD_CHAT = 'AI_NEW_THREAD_CHAT';
export const AI_GET_THREADS = 'AI_GET_THREADS';
export const AI_GET_MESSAGES = 'AI_GET_MESSAGES';
const AI_SEND_MESSAGE_TO_THREAD = 'AI_SEND_MESSAGE_TO_THREAD';
const AI_SEND_FEEDBACK = 'AI_SEND_FEEDBACK';
const AI_RENAME_THREAD = 'AI_RENAME_THREAD';
const AI_DELETE_THREAD = 'AI_DELETE_THREAD';

const appAiApiUrl = process.env.REACT_APP_AI_API_URL || '';

const getAuthHeaders = async (assistant?: string): Promise<Record<string, string>> => {
  const authorization = await Http.getValidAccessToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${authorization}`,
    'Content-Type': 'application/json'
  };
  if (assistant) {
    headers['Assistant'] = assistant;
  }
  return headers;
};

export interface NewThreadChatResponse {
  threadId: string;
  response: string;
  messageId: string;
}

export interface StreamMessageResponse {
  threadId: string;
  content: string;
}

export interface Thread {
  threadId: string;
  userId: string;
  constructionSiteId: number;
  resume: string;
  createdAt: string;
}

export enum MessageRole {
  USER = 0,
  BOT = 1
}

export interface AIChatMessage {
  messageId: string;
  role: MessageRole;
  content: string;
}

export interface AIChatMessageResponse {
  threadId: string;
  userId: string;
  constructionSiteId: number;
  resume: string;
  createdAt: string;
  messages: AIChatMessage[];
}

/** O `resume` da thread pode vir com o contexto anexado após `\n\n[`. */
export const getThreadTitle = (thread: Thread) => thread.resume.split('\n\n[')[0].trim();

export const getThreadsQueryKey = (constructionSiteId: number) => [
  AI_GET_THREADS,
  constructionSiteId
];

export const useSendFeedback = () => {
  return useMutation({
    mutationKey: [AI_SEND_FEEDBACK],
    mutationFn: async (body: {
      comments: string;
      threadId: string;
      messageId: string;
    }): Promise<{ success: boolean }> => {
      const response = await fetch(`${appAiApiUrl}/Feedback`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          Comments: body.comments,
          ThreadId: body.threadId,
          MessageId: body.messageId
        })
      });

      return { success: response.ok };
    }
  });
};

export const useSendNewThreadChat = (constructionSiteId: number, assistant?: string) => {
  return useMutation({
    mutationKey: [AI_NEW_THREAD_CHAT],
    mutationFn: async (message: string): Promise<NewThreadChatResponse> => {
      const response = await fetch(`${appAiApiUrl}/Chat/${constructionSiteId}/new`, {
        method: 'POST',
        headers: await getAuthHeaders(assistant),
        body: JSON.stringify(message)
      });

      if (!response.ok) {
        throw new Error('Falha ao criar a thread');
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getThreadsQueryKey(constructionSiteId) });
    }
  });
};

const STREAM_TIMEOUT_MS = 60000;

export const useSendMessageToThread = (
  constructionSiteId: number,
  assistant?: string
) => {
  return useMutation({
    mutationKey: [AI_SEND_MESSAGE_TO_THREAD],
    mutationFn: async ({
      threadId,
      message,
      onChunk
    }: {
      threadId: string;
      message: string;
      onChunk: (chunk: string) => void;
    }): Promise<StreamMessageResponse> => {
      const headers = await getAuthHeaders(assistant);
      headers['Accept'] = 'text/plain';

      const response = await fetch(
        `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}/stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(message),
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao enviar mensagem para a thread');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder('utf-8');
      const startTime = Date.now();
      let fullContent = '';

      if (reader) {
        try {
          while (Date.now() - startTime < STREAM_TIMEOUT_MS) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            if (chunk) {
              fullContent += chunk;
              onChunk(chunk);
            }
          }

          const remaining = decoder.decode();
          if (remaining) {
            fullContent += remaining;
            onChunk(remaining);
          }
        } finally {
          reader.releaseLock();
        }
      }

      return { threadId, content: fullContent };
    },
    onSuccess: ({ threadId }) => {
      // Recarrega a thread para trocar os ids locais pelos ids reais (usados no feedback)
      queryClient.invalidateQueries({ queryKey: [AI_GET_MESSAGES, threadId] });
    }
  });
};

export const useGetThreads = (constructionSiteId: number) => {
  return useQuery<Thread[]>({
    queryKey: getThreadsQueryKey(constructionSiteId),
    queryFn: async (): Promise<Thread[]> => {
      const response = await fetch(`${appAiApiUrl}/Chat/${constructionSiteId}/thread`, {
        method: 'GET',
        headers: await getAuthHeaders()
      });

      if (!response.ok) {
        return [];
      }

      return await response.json();
    }
  });
};

export const useGetMessages = (constructionSiteId: number, threadId?: string) => {
  return useQuery<AIChatMessageResponse | null>({
    queryKey: [AI_GET_MESSAGES, threadId],
    enabled: Boolean(threadId),
    queryFn: async (): Promise<AIChatMessageResponse | null> => {
      const response = await fetch(
        `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}`,
        {
          method: 'GET',
          headers: await getAuthHeaders()
        }
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    }
  });
};

export const useRenameThread = (constructionSiteId: number) => {
  const queryKey = getThreadsQueryKey(constructionSiteId);

  return useMutation({
    mutationKey: [AI_RENAME_THREAD],
    mutationFn: async ({ threadId, name }: { threadId: string; name: string }) => {
      const response = await fetch(
        `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}`,
        {
          method: 'PATCH',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ Resume: name })
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao renomear a thread');
      }
    },
    onMutate: async ({ threadId, name }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Thread[]>(queryKey);
      queryClient.setQueryData<Thread[]>(queryKey, (threads?: Thread[]) =>
        threads?.map((thread: Thread) =>
          thread.threadId === threadId ? { ...thread, resume: name } : thread
        )
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
};

export const useDeleteThread = (constructionSiteId: number) => {
  const queryKey = getThreadsQueryKey(constructionSiteId);

  return useMutation({
    mutationKey: [AI_DELETE_THREAD],
    mutationFn: async (threadId: string) => {
      const response = await fetch(
        `${appAiApiUrl}/Chat/${constructionSiteId}/thread/${threadId}`,
        {
          method: 'DELETE',
          headers: await getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao excluir a thread');
      }
    },
    onSuccess: (_data, threadId) => {
      queryClient.setQueryData<Thread[]>(queryKey, (threads?: Thread[]) =>
        threads?.filter((thread: Thread) => thread.threadId !== threadId)
      );
      queryClient.removeQueries({ queryKey: [AI_GET_MESSAGES, threadId] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });
};
